import { Fragment, useMemo, useState } from "react";
import { ChevronDown, ChevronRight } from "lucide-react";
import { StatusPill } from "@/components/StatusPill";
import { Button } from "@/components/ui/button";
import { fmtNum } from "@/lib/format";

const TYPES = [["MATERIAL", "Material"], ["LABOUR", "Labour"], ["SUBCONTRACT", "Subcontract"], ["PLANT", "Equipment"], ["OTHER", "Other"]];
const CATS = [["ENGINEERING", "Engineering"], ["PROCUREMENT", "Procurement"], ["CONSTRUCTION", "Construction"], ["NONE", "Not linked to an activity"]];

/** Budget of one estimate version, activity by activity, split into material / labour / subcontract / equipment. */
export function EstimateByActivity({ estimates, wbsItems }) {
  const [versionId, setVersionId] = useState(null);
  const [open, setOpen] = useState({});
  const est = estimates.find((e) => e.id === versionId) || estimates[0];

  const model = useMemo(() => {
    if (!est) return null;
    const byItem = new Map(wbsItems.map((w) => [w.id, w]));
    const rows = new Map();
    for (const l of est.lines) {
      const key = l.wbs_item_id && byItem.has(l.wbs_item_id) ? l.wbs_item_id : "none";
      if (!rows.has(key)) rows.set(key, { key, item: byItem.get(key) || null, by: {}, total: 0, lines: [] });
      const r = rows.get(key);
      const amt = Number(l.amount || 0);
      r.by[l.category] = (r.by[l.category] || 0) + amt;
      r.total += amt;
      r.lines.push(l);
    }
    const typeTotals = {};
    let grand = 0;
    rows.forEach((r) => { grand += r.total; Object.entries(r.by).forEach(([k, v]) => { typeTotals[k] = (typeTotals[k] || 0) + v; }); });
    const shown = TYPES.filter(([k]) => typeTotals[k] > 0);
    const sections = CATS.map(([cat, label]) => {
      const list = [...rows.values()]
        .filter((r) => (cat === "NONE" ? !r.item : r.item && r.item.category === cat))
        .sort((a, b) => (a.item?.code || "").localeCompare(b.item?.code || "", undefined, { numeric: true }));
      return { cat, label, list, total: list.reduce((s, r) => s + r.total, 0) };
    }).filter((s) => s.list.length);
    return { sections, typeTotals, shown, grand };
  }, [est, wbsItems]);

  if (!est || !model) return null;
  const typeLabel = (k) => (TYPES.find((t) => t[0] === k) || [k, k])[1];

  return (
    <div className="rounded-xl border border-border bg-card" data-testid="estimate-by-activity">
      <div className="flex flex-wrap items-center justify-between gap-2 border-b border-border p-4">
        <div>
          <h2 className="text-sm font-semibold uppercase tracking-wider text-muted-foreground">Estimate by activity</h2>
          <p className="mt-0.5 text-xs text-muted-foreground">Each activity&apos;s budget split by cost type. Click a row to see its lines.</p>
        </div>
        <div className="flex flex-wrap items-center gap-1.5">
          {estimates.map((e) => (
            <Button key={e.id} size="sm" variant={e.id === est.id ? "default" : "outline"} onClick={() => setVersionId(e.id)}>
              v{e.version} <span className="ml-1.5 text-[10px] opacity-80">{e.status}</span>
            </Button>
          ))}
        </div>
      </div>

      <div className="overflow-x-auto">
        <table className="w-full text-sm">
          <thead className="bg-muted/40 text-xs text-muted-foreground">
            <tr>
              <th className="w-8 px-2 py-2" />
              <th className="px-3 py-2 text-left font-medium">Code</th>
              <th className="px-3 py-2 text-left font-medium">Activity</th>
              <th className="px-3 py-2 text-right font-medium">Qty</th>
              {model.shown.map(([k, l]) => <th key={k} className="px-3 py-2 text-right font-medium">{l}</th>)}
              <th className="px-3 py-2 text-right font-medium">Total</th>
            </tr>
          </thead>
          <tbody>
            {model.sections.map((s) => (
              <SectionRows key={s.cat} s={s} shown={model.shown} open={open} setOpen={setOpen} typeLabel={typeLabel} />
            ))}
            <tr className="border-t-2 border-border bg-muted/30 font-semibold">
              <td colSpan={4} className="px-3 py-2 text-right">Total budget · v{est.version} <span className="ml-2 align-middle"><StatusPill value={est.status} /></span></td>
              {model.shown.map(([k]) => <td key={k} className="px-3 py-2 text-right font-mono text-xs">{fmtNum(model.typeTotals[k] || 0, 2)}</td>)}
              <td className="px-3 py-2 text-right font-mono text-xs" data-testid="estimate-by-activity-total">{fmtNum(model.grand, 2)}</td>
            </tr>
          </tbody>
        </table>
      </div>
    </div>
  );
}

function SectionRows({ s, shown, open, setOpen, typeLabel }) {
  const span = 5 + shown.length;
  return (
    <>
      <tr className="bg-muted/20">
        <td colSpan={span - 1} className="px-3 py-1.5 text-xs font-semibold uppercase tracking-wider text-muted-foreground">{s.label}</td>
        <td className="px-3 py-1.5 text-right font-mono text-xs font-semibold text-muted-foreground">{fmtNum(s.total, 2)}</td>
      </tr>
      {s.list.map((r) => {
        const isOpen = Boolean(open[r.key]);
        return (
          <Fragment key={r.key}>
            <tr className="cursor-pointer border-t border-border/60 hover:bg-muted/30" onClick={() => setOpen({ ...open, [r.key]: !isOpen })} data-testid={`estimate-activity-row-${r.key}`}>
              <td className="px-2 py-1.5">{isOpen ? <ChevronDown className="h-3.5 w-3.5" /> : <ChevronRight className="h-3.5 w-3.5" />}</td>
              <td className="px-3 py-1.5 font-mono text-xs text-muted-foreground">{r.item?.code || "—"}</td>
              <td className="px-3 py-1.5 text-foreground">{r.item?.name || "Lines not linked to an activity"}</td>
              <td className="px-3 py-1.5 text-right font-mono text-xs text-muted-foreground">{r.item?.quantity != null ? `${fmtNum(r.item.quantity)} ${r.item.uom || ""}` : "—"}</td>
              {shown.map(([k]) => <td key={k} className="px-3 py-1.5 text-right font-mono text-xs">{r.by[k] ? fmtNum(r.by[k], 2) : "—"}</td>)}
              <td className="px-3 py-1.5 text-right font-mono text-xs font-semibold">{fmtNum(r.total, 2)}</td>
            </tr>
            {isOpen && r.lines.map((l) => (
              <tr key={l.id} className="bg-muted/10 text-xs text-muted-foreground">
                <td />
                <td />
                <td className="px-3 py-1" colSpan={2}>{typeLabel(l.category)} · {l.description}</td>
                <td className="px-3 py-1 text-right font-mono" colSpan={shown.length}>{fmtNum(l.quantity)} {l.uom || ""} × {fmtNum(l.rate, 2)}</td>
                <td className="px-3 py-1 text-right font-mono">{fmtNum(l.amount, 2)}</td>
              </tr>
            ))}
          </Fragment>
        );
      })}
    </>
  );
}
